import React, { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { Building2, Mail, UserPlus, X } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';

import {
  DEFAULT_SUBTITLE,
  DEFAULT_TITLE,
  EMAIL_HELPER,
  GENERIC_ERROR,
  LABELS,
  LEGAL_LINKS,
  PLACEHOLDERS,
  SIGN_IN_HREF,
  SIGN_IN_LABEL,
  SUBMIT_LABEL,
  TOGGLE_LABELS
} from './constants';
import type { RequestAccessFormData } from './schema';
import { buildRequestAccessSchema } from './schema';
import SuccessPanel from './SuccessPanel';
import type { TurnstileHandle } from './TurnstileField';
import TurnstileField from './TurnstileField';
import type {
  RequestAccessFormProps,
  RequestAccessSuccessContext
} from './types';
import { useRequestAccess } from './useRequestAccess';

import styles from './styles.module.css';

import { track } from '@/shared/analytics/track';
import { ApiError } from '@/shared/api/client';
import { describeAuthError } from '@/shared/api/errors';
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner';
import { getTurnstileSiteKey } from '@/shared/security/turnstile';
import { extractCompanyName } from '@/shared/utils/workEmail';

// b2b-api returns RFC-7807 problem details, which have NO `message` key — the
// user-facing copy (personal-email rejection, the three duplicate-company 409s,
// rate limiting) is in `detail`. Fall back to `message` for transport-level
// failures, where `detail` is empty.
const errorText = (error: unknown): string => {
  if (error instanceof ApiError) {
    return error.detail || error.message || GENERIC_ERROR;
  }
  if (error instanceof Error) {
    return error.message || GENERIC_ERROR;
  }
  return GENERIC_ERROR;
};

const RequestAccessForm: React.FC<RequestAccessFormProps> = ({
  variant = 'page',
  maxColleagues = 1,
  colleagueMode = 'toggle',
  title = DEFAULT_TITLE,
  subtitle = DEFAULT_SUBTITLE,
  className
}) => {
  const [success, setSuccess] = useState<RequestAccessSuccessContext | null>(
    null
  );
  const companyManuallyEdited = useRef(false);

  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  // Set when the Turnstile script itself could not load (extension, corporate
  // proxy, Cloudflare incident). Ungates submit rather than leaving the site's
  // primary conversion surface permanently dead — b2b-api still rejects the
  // tokenless submission, so this costs no security, only a legible error.
  const [turnstileUnavailable, setTurnstileUnavailable] = useState(false);
  const turnstileRef = useRef<TurnstileHandle>(null);
  const turnstileSiteKey = getTurnstileSiteKey();
  const turnstileRequired = !!turnstileSiteKey && !turnstileUnavailable;

  const schema = useMemo(
    () => buildRequestAccessSchema(maxColleagues),
    [maxColleagues]
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    control,
    formState: { errors }
  } = useForm<RequestAccessFormData>({
    resolver: zodResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: { email: '', companyName: '', colleagueEmails: [] }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'colleagueEmails'
  });

  const emailValue = watch('email');
  const companyValue = watch('companyName');

  useEffect(() => {
    if (companyManuallyEdited.current) return;
    const company = extractCompanyName(emailValue);
    if (company) {
      // Validate on autofill: the derived value is always non-empty, so this can
      // only clear a stale "required" error, never introduce one.
      setValue('companyName', company, { shouldValidate: true });
    }
  }, [emailValue, setValue]);

  useEffect(() => {
    track('request_access_viewed', { variant });
  }, [variant]);

  // Fires once per mount on the first edit to any field. React's onChange on a
  // <form> is the native `input` event, which bubbles from every control, so
  // this needs no per-field wiring and cannot miss the colleague inputs.
  const startedRef = useRef(false);
  const handleFirstEdit = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    track('request_access_started', { variant });
  };

  const mutation = useRequestAccess();

  const onSubmit = (data: RequestAccessFormData) => {
    const colleagueEmails = data.colleagueEmails
      .map((entry) => entry.value.trim())
      .filter(Boolean)
      // The API dedupes across [email, ...colleagueEmails], so a colleague that
      // matches the primary address would 409 the whole submission.
      .filter(
        (value) => value.toLowerCase() !== data.email.trim().toLowerCase()
      );

    // No email or company name in any of these: the funnel needs shape, not
    // identity, and this form is the one place a prospect's address is typed.
    track('request_access_submitted', {
      variant,
      colleague_count: colleagueEmails.length,
      // Tells us whether the extractCompanyName heuristic is actually working:
      // a high rate here means users keep correcting our guess.
      company_name_edited: companyManuallyEdited.current
    });

    mutation.mutate(
      {
        email: data.email,
        companyName: data.companyName,
        colleagueEmails,
        // JSON.stringify drops undefined (see api/client.ts), so a missing
        // token sends no key at all rather than an explicit null.
        turnstileToken: turnstileToken ?? undefined
      },
      {
        onSuccess: (response) => {
          const provisioned = response.data.provisioned ?? false;

          track('request_access_succeeded', {
            variant,
            colleague_count: colleagueEmails.length,
            // The auto-provision vs. manual-review split — the outcome that
            // actually decides whether this prospect can sign in today.
            provisioned
          });

          setSuccess({
            email: data.email,
            companyName: data.companyName,
            colleagueEmails,
            prospectId: response.data.id,
            serverMessage: response.data.message,
            // Default false: an API build without the field must not promise an
            // invitation email that may never arrive.
            provisioned
          });
        },
        onError: (error) => {
          track('request_access_failed', {
            variant,
            colleague_count: colleagueEmails.length,
            ...describeAuthError(error)
          });

          // The server has now consumed this token, valid or not, and tokens
          // are single-use. Reset on EVERY failure, not just a verification
          // one: b2b-api verifies before it validates, so a shape error or a
          // duplicate-company 409 has already spent the token too. Without
          // this, the retry the error copy invites fails server-side with a
          // confusing message.
          //
          // Clear first, then reset — reset re-runs the challenge and its
          // success callback repopulates the token.
          setTurnstileToken(null);
          turnstileRef.current?.reset();
        }
      }
    );
  };

  const rootClasses = classNames(
    styles.root,
    variant === 'hero' ? styles.hero : styles.page,
    className
  );

  // Deliberately asymmetric with the onError path above: there is no widget to
  // reset here. Success swaps the whole form for this panel, so TurnstileField
  // unmounts and its cleanup removes the widget.
  if (success) {
    return (
      <div className={rootClasses}>
        <SuccessPanel context={success} />
      </div>
    );
  }

  const isToggle = colleagueMode === 'toggle';
  const colleagueOpen = fields.length > 0;
  const atCap = fields.length >= maxColleagues;

  // With `mode: 'onBlur'`, tabbing through the empty company field fires
  // "Company name is required" before the user has typed an email — backwards,
  // because companyName is AUTOFILLED from the email domain by the effect above.
  // Suppressing the error at render time rather than making the schema
  // conditional keeps the schema an honest description of a valid payload;
  // submit is blocked independently by `submitDisabled` below.
  const showCompanyError = !!errors.companyName && !!emailValue?.trim();

  // Both fields required before submit is live, plus a solved challenge when a
  // widget is actually on screen and working. This also means the prerendered
  // HTML ships a disabled button, which is correct — a visitor with no JS can't
  // submit anyway, so an enabled button would be a lie.
  //
  // The token clause is UX, not security: a `submit` event dispatched on the
  // button bubbles straight past `disabled`, and a bot skips the browser
  // entirely. Enforcement is 100% server-side, which is also why onSubmit has
  // no hard token guard — one would buy nothing and swallow the submit
  // silently.
  const submitDisabled =
    mutation.isPending ||
    !emailValue?.trim() ||
    !companyValue?.trim() ||
    (turnstileRequired && !turnstileToken);

  // Guards against the live field array rather than the render-time snapshot:
  // several clicks inside one React batch all see the same stale fields.length
  // and would otherwise append past the cap.
  const addColleague = () => {
    if ((getValues('colleagueEmails')?.length ?? 0) >= maxColleagues) return;
    append({ value: '' });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onChange={handleFirstEdit}
      className={rootClasses}
      noValidate={true}
    >
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.subtitle}>{subtitle}</p>

      <div className={styles.field}>
        <label htmlFor="ra-email" className={styles.label}>
          {LABELS.email}
        </label>
        <div
          className={classNames(styles.input, {
            [styles.invalid]: !!errors.email
          })}
        >
          <Mail size={18} aria-hidden="true" />
          <input
            id="ra-email"
            type="email"
            autoComplete="email"
            placeholder={PLACEHOLDERS.email}
            {...register('email')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'ra-email-error' : 'ra-email-help'}
          />
        </div>
        {errors.email ? (
          <p id="ra-email-error" className={styles.error} role="alert">
            {errors.email.message}
          </p>
        ) : (
          <span id="ra-email-help" className={styles.help}>
            {EMAIL_HELPER}
          </span>
        )}
      </div>

      <div className={styles.field}>
        <label htmlFor="ra-company" className={styles.label}>
          {LABELS.company}
        </label>
        <div
          className={classNames(styles.input, {
            [styles.invalid]: showCompanyError
          })}
        >
          <Building2 size={18} aria-hidden="true" />
          <input
            id="ra-company"
            type="text"
            autoComplete="organization"
            placeholder={PLACEHOLDERS.company}
            {...register('companyName', {
              onChange: () => {
                companyManuallyEdited.current = true;
              }
            })}
            aria-invalid={showCompanyError}
            aria-describedby={showCompanyError ? 'ra-company-error' : undefined}
          />
        </div>
        {showCompanyError && (
          <p id="ra-company-error" className={styles.error} role="alert">
            {errors.companyName?.message}
          </p>
        )}
      </div>

      {fields.length > 0 && (
        <div className={styles.colleagues}>
          {fields.map((field, index) => (
            <div className={styles.field} key={field.id}>
              <label htmlFor={`ra-colleague-${index}`} className={styles.label}>
                {maxColleagues > 1
                  ? `${LABELS.colleague} ${index + 1}`
                  : LABELS.colleague}
              </label>
              <div className={styles.colleagueRow}>
                <div
                  className={classNames(styles.input, {
                    [styles.invalid]: !!errors.colleagueEmails?.[index]?.value
                  })}
                >
                  <Mail size={18} aria-hidden="true" />
                  <input
                    id={`ra-colleague-${index}`}
                    type="email"
                    autoComplete="off"
                    placeholder={PLACEHOLDERS.colleague}
                    {...register(`colleagueEmails.${index}.value` as const)}
                    aria-invalid={!!errors.colleagueEmails?.[index]?.value}
                  />
                </div>
                {!isToggle && (
                  <button
                    type="button"
                    className={styles.removeColleague}
                    onClick={() => remove(index)}
                    aria-label={`Remove colleague ${index + 1}`}
                  >
                    <X size={16} aria-hidden="true" />
                  </button>
                )}
              </div>
              {errors.colleagueEmails?.[index]?.value && (
                <p className={styles.error} role="alert">
                  {errors.colleagueEmails[index]?.value?.message}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {isToggle ? (
        <button
          type="button"
          className={styles.addColleague}
          aria-expanded={colleagueOpen}
          aria-controls="ra-colleague-0"
          onClick={() => {
            if (colleagueOpen) {
              remove(0);
              return;
            }
            addColleague();
            // Revealing a field without moving focus leaves keyboard users
            // stranded on the toggle.
            requestAnimationFrame(() =>
              document.getElementById('ra-colleague-0')?.focus()
            );
          }}
        >
          <UserPlus size={18} aria-hidden="true" />
          <span>
            {colleagueOpen ? TOGGLE_LABELS.remove : TOGGLE_LABELS.add}
          </span>
        </button>
      ) : (
        !atCap && (
          <button
            type="button"
            className={styles.addColleague}
            onClick={addColleague}
          >
            <UserPlus size={18} aria-hidden="true" />
            <span>{TOGGLE_LABELS.add}</span>
          </button>
        )
      )}

      {/* Above the server-error block, so that error stays adjacent to the
          button that produced it. `setTurnstileToken` is passed bare because a
          stable identity is what keeps the widget from re-rendering per
          keystroke — see the latest-ref note in TurnstileField. */}
      {turnstileSiteKey && !turnstileUnavailable && (
        <TurnstileField
          ref={turnstileRef}
          siteKey={turnstileSiteKey}
          variant={variant}
          onChange={setTurnstileToken}
          onUnavailable={() => {
            setTurnstileToken(null);
            setTurnstileUnavailable(true);
          }}
        />
      )}

      {mutation.isError && (
        <p className={styles.error} role="alert">
          {errorText(mutation.error)}
        </p>
      )}

      <button
        type="submit"
        className={styles.submit}
        disabled={submitDisabled}
        aria-busy={mutation.isPending}
      >
        {mutation.isPending ? (
          <>
            <LoadingSpinner size="sm" className={styles.submitSpinner} />
            <span>Sending…</span>
          </>
        ) : (
          SUBMIT_LABEL
        )}
      </button>

      <a
        className={styles.signIn}
        href={SIGN_IN_HREF}
        onClick={() =>
          track(
            'sign_in_clicked',
            { location: 'request_access_form', variant },
            { beforeNavigation: true }
          )
        }
      >
        {SIGN_IN_LABEL}
      </a>

      <p className={styles.legal}>
        By requesting access you agree to ThredUp&apos;s{' '}
        <a href={LEGAL_LINKS.terms} target="_blank" rel="noopener noreferrer">
          Terms of Use
        </a>{' '}
        and{' '}
        <a href={LEGAL_LINKS.privacy} target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
        .
      </p>
    </form>
  );
};

export default RequestAccessForm;
